import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Drawer,
  IconButton,
  Menu,
  MenuItem,
  styled,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  useTheme,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Typography from '@mui/material/Typography';
import useBreakpoint from '../../../../hooks/useBreakpoint';
import { ADDRESSBOOK, STRINGS } from '../../../../constants';
import addressBooksSlice from '../../../../redux/addressBooksSlice';
import { useCustomSnackbar } from '../../../../features/CustomSnackbar';
import useErrorHandler from '../../../../hooks/useErrorHandler';
import { AddressBookDeleteModal, AddressBookForm } from '../components';
import { useOverlay } from '../../../../features/Overlay';
import { AddressBookHelpers } from '../../../../helpers';

const CHECKBOX_COLUMN_HEADINGS = ['Default', 'Billing'];

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  fontSize: '14px',
  minHeight: '36px',
  '&:hover': {
    backgroundColor: 'transparent',
    color: theme.vars.palette.primary.main,
  },
}));

const StyledTypography = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  fontFamily: 'Roboto',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '24px',
  letterSpacing: '0.17px',
  color: theme.vars.palette.text.secondary,
  '&:first-child': {
    color: theme.vars.palette.text.primary,
    marginBottom: '2px',
  },
  '&:nth-child(2)': {
    fontWeight: 700,
  },
}));

const StyledTableCell = styled(TableCell)(
  ({ theme, isSmallScreen, heading }) => ({
    paddingTop: '6px',
    paddingBottom: '6px',
    paddingLeft: CHECKBOX_COLUMN_HEADINGS.includes(heading) ? '8px' : '16px',
    paddingRight: CHECKBOX_COLUMN_HEADINGS.includes(heading) ? '8px' : '16px',
    alignContent: isSmallScreen ? 'start' : 'center',
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
    [theme.breakpoints.up('md')]: {
      whiteSpace: 'normal',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: '120px',

      '&:first-child': {
        minWidth: '56px',
        width: '56px',
        paddingLeft: 0,
        paddingRight: '8px',
        whiteSpace: 'normal',
      },
      '&:nth-child(2), &:nth-child(3)': {
        minWidth: '134px',
        width: '134px',
        wordBreak: 'break-all',
      },
    },
    '&:last-child': {
      minWidth: '40px',
      width: '40px',
      paddingLeft: 0,
      paddingRight: '14px',
    },
  })
);

const ActionMenuBtn = ({ row, actionMenuBtnHandleClick }) => (
  <IconButton
    sx={{ p: 0 }}
    aria-label='more'
    onClick={e => actionMenuBtnHandleClick(e, row)}
  >
    <MoreVertIcon />
  </IconButton>
);

const ActionSelectBtn = ({ row, actionSelectBtnHandleClick, resetSearch }) => (
  <Button
    sx={{ mx: 0, my: 1 }}
    variant='outlined'
    color='primary'
    onClick={() => {
      actionSelectBtnHandleClick(row);
      resetSearch();
    }}
  >
    {STRINGS.SELECT_ADDRESS}
  </Button>
);

const getTableCellContent = (
  column,
  row,
  actionEditBtnHandleClick,
  onSelect,
  resetSearch
) => {
  switch (column.path) {
    case 'actionMenuBtn':
      return (
        <ActionMenuBtn
          row={row}
          actionMenuBtnHandleClick={actionEditBtnHandleClick}
        />
      );
    case 'actionSelectedButton':
      return (
        <ActionSelectBtn
          row={row}
          actionSelectBtnHandleClick={onSelect}
          resetSearch={resetSearch}
        />
      );
    default:
      if (column.content) {
        return column.content.map(contentColumn => (
          <StyledTypography variant='body2' key={contentColumn.path}>
            {contentColumn.getter
              ? contentColumn.getter(row, contentColumn.path)
              : row[contentColumn.path]}
          </StyledTypography>
        ));
      }
      return column.getter ? column.getter(row, column.path) : row[column.path];
  }
};

const AddressBookTable = ({
  rows = [],
  onEditRowHandler,
  currentPage,
  setPaginationParameters,
  setTotalPages,
  onSelect,
  searchPageSize,
  config,
  isSidebarOpen,
  isEditMode,
  isSearchMode,
  resetSearch = () => {},
  styles = {},
  customization,
}) => {
  const [selectedAddressBook, setSelectedAddressBook] = useState(
    ADDRESSBOOK.ADDRESS_BOOK_INITIAL_FORM_VALUES
  );
  const dispatch = useDispatch();
  const theme = useTheme();
  const isSmallScreen = useBreakpoint('md');

  const { showSuccess } = useCustomSnackbar();
  const overlay = useOverlay();
  const { errorHandler } = useErrorHandler();

  const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState(null);
  const [currentRow, setCurrentRow] = useState(null);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);

  const actionEditBtnHandleClick = (e, row) => {
    setActionMenuAnchorEl(e.currentTarget);
    setCurrentRow(row);
  };

  const actionMenuBtnHandleClose = () => {
    setActionMenuAnchorEl(null);
  };

  const handleDelete = useCallback(async () => {
    if (currentRow) {
      try {
        overlay.show();

        await dispatch(
          addressBooksSlice.actions.deleteAddressBook(currentRow.addressBookId)
        ).unwrap();

        // if the last addressBook item on the page is being deleted,
        // fetch addressBooks for the prev page
        if (rows.length === 1) {
          const prevPageNumber = currentPage - 1;
          const searchPage = prevPageNumber > 1 ? prevPageNumber : 1;
          await dispatch(
            addressBooksSlice.actions.fetchAddressBooks({
              searchPage,
              searchPageSize,
            })
          );
          setPaginationParameters &&
            setPaginationParameters({ currentPage: searchPage });
        } else {
          await dispatch(
            addressBooksSlice.actions.fetchAddressBooks({
              searchPage: currentPage,
              searchPageSize,
            })
          );
        }

        showSuccess({
          message: STRINGS.ADDRESSES_SECTION.NOTIFICATIONS.SUCCESS.DELETE,
          variant: 'default',
        });
      } catch (error) {
        errorHandler({
          message: STRINGS.ADDRESSES_SECTION.NOTIFICATIONS.ERROR.DELETE,
          error,
          component: 'AddressBookTable',
        });
      } finally {
        overlay.hide();
        resetSearch();
      }
    }
  }, [
    currentRow,
    dispatch,
    overlay,
    rows.length,
    showSuccess,
    currentPage,
    setPaginationParameters,
    searchPageSize,
    errorHandler,
    resetSearch,
  ]);

  const handleEdit = useCallback(async () => {
    if (currentRow) {
      try {
        overlay.show();
        actionMenuBtnHandleClose();
        const addressBook = await dispatch(
          addressBooksSlice.actions.getAddressBookById(currentRow.addressBookId)
        ).unwrap();
        setSelectedAddressBook(
          AddressBookHelpers.normalizeFromResponse(addressBook)
        );
        onEditRowHandler(true);
      } catch (error) {
        errorHandler({
          message: STRINGS.ADDRESSES_SECTION.NOTIFICATIONS.ERROR.RETRIEVE,
          error,
          component: 'AddressBookTable',
        });
      } finally {
        overlay.hide();
      }
    }
  }, [currentRow, overlay, dispatch, onEditRowHandler, errorHandler]);

  return (
    <>
      {!rows.length ? (
        <Box
          sx={{
            textAlign: 'center',
            mt: 1,
            color: theme.vars.palette.text.secondary,
            fontSize: '12px',
          }}
        >
          {isSearchMode
            ? STRINGS.ADDRESSES_SECTION.NO_ADDRESSES_FOUND
            : STRINGS.ADDRESSES_SECTION.NO_ADDRESSES_ADDED}
        </Box>
      ) : (
        <Box sx={{ ...styles.container }}>
          <Table aria-label='addresses table' size='small'>
            <TableHead sx={{ ...styles.head }}>
              <TableRow>
                {(isSmallScreen
                  ? config.columns.mobile
                  : config.columns.desktop
                ).map(({ heading }, index) => (
                  <StyledTableCell
                    key={index}
                    heading={heading}
                    isSmallScreen={isSmallScreen}
                    sx={{
                      color: theme.vars.palette.text.secondary,
                      fontSize: '12px',
                    }}
                  >
                    {heading}
                  </StyledTableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, rowIndex) => (
                <TableRow
                  key={row.addressBookId}
                  sx={{
                    '&:last-child td, &:last-child th': !isSmallScreen && {
                      border: 0,
                    },
                    '&:hover': {
                      backgroundColor: theme.vars.palette.action.hover,
                    },
                    backgroundColor:
                      currentRow?.addressBookId === row.addressBookId &&
                      !!actionMenuAnchorEl
                        ? theme.vars.palette.action.hover
                        : 'inherit',
                    verticalAlign: 'top',
                    ...customization?.rowStyle(row),
                  }}
                >
                  {(isSmallScreen
                    ? config.columns.mobile
                    : config.columns.desktop
                  ).map((column, columnIndex) => (
                    <StyledTableCell
                      key={`${column.path}_${columnIndex}`}
                      heading={column.heading}
                      isSmallScreen={isSmallScreen}
                      sx={{ ...styles.tableCell }}
                      data-testid={`${rowIndex}_addressBook.${column.path}_${columnIndex}`}
                    >
                      <Box
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: { md: '-webkit-box', xs: 'block' },
                          WebkitLineClamp: '2',
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {getTableCellContent(
                          column,
                          row,
                          actionEditBtnHandleClick,
                          onSelect,
                          resetSearch
                        )}
                      </Box>
                    </StyledTableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Menu
            anchorEl={actionMenuAnchorEl}
            open={!!actionMenuAnchorEl}
            onClose={actionMenuBtnHandleClose}
            MenuListProps={{ sx: { width: '124px' } }}
            anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
            transformOrigin={{ horizontal: 120, vertical: 90 }}
          >
            <StyledMenuItem disableRipple onClick={handleEdit}>
              {STRINGS.ADDRESSES_SECTION.TABLE_ACTIONS.EDIT}
            </StyledMenuItem>
            <StyledMenuItem
              disableRipple
              onClick={() => {
                setDeleteModalOpen(true);
                actionMenuBtnHandleClose();
              }}
            >
              {STRINGS.ADDRESSES_SECTION.TABLE_ACTIONS.DELETE}
            </StyledMenuItem>
          </Menu>
          <AddressBookDeleteModal
            isOpen={isDeleteModalOpen}
            onClose={() => setDeleteModalOpen(false)}
            onDelete={handleDelete}
            contactName={currentRow?.contactName}
          />
        </Box>
      )}
      <Drawer
        anchor='right'
        open={isSidebarOpen}
        onClose={(_, reason) => {
          if (reason === 'backdropClick') {
            return;
          }
          onEditRowHandler(false);
          setSelectedAddressBook(ADDRESSBOOK.ADDRESS_BOOK_INITIAL_FORM_VALUES);
        }}
        PaperProps={{ sx: { width: { xs: '100%', sm: 450 } } }}
      >
        <AddressBookForm
          onSidebarClose={() => {
            onEditRowHandler(false);
            setSelectedAddressBook(
              ADDRESSBOOK.ADDRESS_BOOK_INITIAL_FORM_VALUES
            );
          }}
          isEditMode={isEditMode}
          currentPage={currentPage}
          setPaginationParameters={setPaginationParameters}
          setTotalPages={setTotalPages}
          searchPageSize={searchPageSize}
          resetSearch={resetSearch}
          selectedAddressBook={selectedAddressBook}
          setSelectedAddressBook={setSelectedAddressBook}
        />
      </Drawer>
    </>
  );
};

AddressBookTable.propTypes = {
  rows: PropTypes.array.isRequired,
  onEditRowHandler: PropTypes.func,
  currentPage: PropTypes.number,
  setPaginationParameters: PropTypes.func,
  setTotalPages: PropTypes.func,
  isEditMode: PropTypes.bool,
  isSearchMode: PropTypes.bool,
  resetSearch: PropTypes.func,
  isSidebarOpen: PropTypes.bool,
  style: PropTypes.object,
  customization: PropTypes.shape({
    rowStyle: PropTypes.func,
  }),
};

export default AddressBookTable;
